import { memo, useState, useEffect, useRef } from "react";
import { X, Play, Pause, Volume2, VolumeX, Flag, Ban, MoreVertical } from "lucide-react";
import { Profile } from "@/types/profile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import VideoReportModal from "@/components/profile/VideoReportModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

interface VideoModalProps {
  profile: Profile;
  isOpen: boolean;
  onClose: () => void;
}

const VideoModal = memo(({ profile, isOpen, onClose }: VideoModalProps) => {
  // Get display name: username if available, otherwise first name
  const getDisplayName = () => {
    if (profile.username) return profile.username;
    if (profile.name) return profile.name.split(' ')[0];
    return 'User';
  };
  const { user } = useAuthContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true); // Start muted for auto-play
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  // Auto-play muted when modal opens
  useEffect(() => {
    if (isOpen && videoRef.current && profile.videoUrl) {
      videoRef.current.currentTime = 0;
      setProgress(0);
      setIsMuted(true); // Always start muted
      // Auto-play after short delay
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().then(() => {
            setIsPlaying(true);
          }).catch(() => {
            // Browser blocked auto-play, that's fine
            setIsPlaying(false);
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, profile.videoUrl]);

  // Handle video time update
  const handleTimeUpdate = () => {
    if (videoRef.current && duration > 0) {
      setProgress((videoRef.current.currentTime / duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const togglePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleBlock = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("blocked_users")
        .insert({
          blocker_id: user.id,
          blocked_id: profile.id,
          reason: "Blocked from video modal",
        });

      if (error) throw error;

      toast({ title: "User blocked", description: `You won't see ${profile.name} anymore.` });
      setShowBlockConfirm(false);
      onClose();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black animate-scale-in"
        onClick={onClose}
      >
        <div 
          className="relative w-full h-full max-w-md mx-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar with close and menu */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-20">
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* More options menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                <DropdownMenuItem 
                  onClick={() => setShowReportModal(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Report Video
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setShowBlockConfirm(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Block User
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {profile.hasVideo && profile.videoUrl ? (
            <>
              {/* Actual video player */}
              <div className="relative w-full h-full">
                <video
                  ref={videoRef}
                  src={profile.videoUrl}
                  className="w-full h-full object-cover"
                  playsInline
                  muted={isMuted}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                  onClick={togglePlayPause}
                />
                
                {/* Overlay for gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50 pointer-events-none" />
                
                {/* Play/pause overlay */}
                {!isPlaying && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={togglePlayPause}
                  >
                    <div className="w-20 h-20 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                      <Play className="w-10 h-10 text-white fill-current ml-1" />
                    </div>
                  </div>
                )}

                {/* Progress bar */}
                <div className="absolute bottom-20 left-4 right-4 h-1 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-pop rounded-full transition-all duration-150"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {/* Mute toggle with tap hint */}
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="absolute bottom-24 right-4 flex items-center gap-2 px-3 py-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                >
                  {isMuted ? (
                    <>
                      <VolumeX className="w-5 h-5" />
                      <span className="text-xs">Tap for sound</span>
                    </>
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </button>

                {/* Profile info overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex items-end gap-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white flex-shrink-0">
                    <img
                      src={profile.photos[0]}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white text-xl font-bold text-shadow-soft">
                      {getDisplayName()}, {profile.age}
                    </h3>
                    <p className="text-white/80 text-sm mt-0.5 line-clamp-2">
                      {profile.bio}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* No video state */
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 to-black">
              {/* Profile info */}
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20 mb-4">
                <img
                  src={profile.photos[0]}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-white/60 text-sm mb-6">{profile.name}</p>

              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mb-6">
                <Play className="w-12 h-12 text-white/50" />
              </div>
              <h3 className="text-white text-xl font-semibold mb-2">No Video Yet</h3>
              <p className="text-white/60 text-center px-8">
                {profile.name} hasn't added an intro video yet.
              </p>
              <button
                onClick={onClose}
                className="mt-8 px-6 py-3 rounded-full bg-pop text-white font-medium hover:scale-105 transition-transform"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      <VideoReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        userId={profile.id}
        userName={getDisplayName()}
      />

      {/* Block Confirmation */}
      <AlertDialog open={showBlockConfirm} onOpenChange={setShowBlockConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block {profile.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You won't see this user's profile or videos anymore. They won't be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlock}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

VideoModal.displayName = "VideoModal";

export default VideoModal;
